import React from "react";
import styled, { keyframes } from "styled-components";
import { narrowScreenMediaQuery } from "../constants/styles";
import { Skeleton } from "./Skeleton";

// Paged carousel: one page at a time, switching triggers a quick fade.
//
// Arrows sit on the sides of the card row (vertically centered, flanking
// the viewport) — the common content-carousel pattern. Dots sit beneath
// the cards and are *windowed*: at most `MAX_VISIBLE_DOTS` dots render at
// any time, sliding to keep the active dot in view.
//
// Props:
//   items          — current page's items (array, length ≤ perPage)
//   perPage        — how many slots in the current layout (1 on narrow, N on wide)
//   pageIdx        — current page index (0-based)
//   totalPages     — total pages
//   onPageChange   — (nextIdx) => void
//   loading        — boolean; when true, renders skeleton cells
//   renderItem     — (item, idx) => ReactNode
//   renderSkeleton — optional (slotIdx) => ReactNode for custom skeleton cells
//   ariaLabel      — accessibility label
//   emptyMessage   — optional string shown when loading=false and items is empty

const MAX_VISIBLE_DOTS = 5;

const fadeIn = keyframes`
  from { opacity: 0.4; }
  to   { opacity: 1; }
`;

const Wrap = styled.div`
  position: relative;
`;

const Viewport = styled.div`
  overflow: hidden;
`;

const Track = styled.div`
  display: grid;
  grid-template-columns: repeat(${({ $perPage }) => $perPage}, minmax(0, 1fr));
  column-gap: 20px;
  /* When items are subgrids spanning N rows, each card sets its own inner
   * row-gap; the track must not add row-gap between those rows. Non-subgrid
   * sections keep the normal single-row gap.
   */
  row-gap: ${({ $subgridRows }) => ($subgridRows ? "0" : "20px")};
  animation: ${fadeIn} 280ms ease;

  @media ${narrowScreenMediaQuery} {
    column-gap: 16px;
    row-gap: ${({ $subgridRows }) => ($subgridRows ? "0" : "16px")};
  }
`;

// Default skeleton cell — used when a section doesn't pass a custom
// `renderSkeleton`. Uses the shared shimmer so loading placeholders feel
// alive instead of dead-grey rectangles.
function SkeletonCell() {
  return <Skeleton $radius="12px" style={{ aspectRatio: "16 / 10" }} />;
}

const EmptyState = styled.div`
  padding: 32px 16px;
  text-align: center;
  color: #6b7280;
  font-size: 13px;
  border: 1px dashed rgba(0, 0, 0, 0.1);
  border-radius: 12px;

  [data-theme="dark"] & {
    color: rgba(255, 255, 255, 0.6);
    border-color: rgba(255, 255, 255, 0.12);
  }
`;

const SideArrow = styled.button`
  position: absolute;
  top: 50%;
  transform: translateY(-50%);
  ${({ $dir }) => ($dir === "prev" ? "left: -20px;" : "right: -20px;")}
  z-index: 2;
  background: #ffffff;
  color: #1f2937;
  border: 1px solid #e5e7eb;
  width: 40px;
  height: 40px;
  border-radius: 50%;
  cursor: pointer;
  font-size: 20px;
  line-height: 1;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
  transition:
    background 150ms ease,
    color 150ms ease,
    transform 120ms ease,
    box-shadow 150ms ease,
    border-color 150ms ease;

  &:hover:not(:disabled) {
    background: #d85907;
    border-color: #d85907;
    color: #fff;
    transform: translateY(-50%) scale(1.06);
    box-shadow: 0 8px 22px rgba(216, 89, 7, 0.35);
  }

  &:disabled {
    opacity: 0.35;
    cursor: not-allowed;
  }

  &:focus-visible {
    outline: 2px solid #d85907;
    outline-offset: 2px;
  }

  @media ${narrowScreenMediaQuery} {
    width: 34px;
    height: 34px;
    font-size: 18px;
    ${({ $dir }) => ($dir === "prev" ? "left: 4px;" : "right: 4px;")}
  }

  [data-theme="dark"] & {
    background: #17171e;
    border-color: rgba(255, 255, 255, 0.1);
    color: #f3f4f6;
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.45);
  }
`;

const Controls = styled.div`
  margin-top: 24px;
  display: flex;
  justify-content: center;
`;

const Dots = styled.div`
  display: inline-flex;
  gap: 8px;
  align-items: center;
`;

const Dot = styled.button`
  border: none;
  width: ${({ $active }) => ($active ? "28px" : "10px")};
  height: 10px;
  border-radius: 999px;
  background: ${({ $active }) => ($active ? "#D85907" : "#d1d5db")};
  cursor: pointer;
  transition: all 180ms ease;
  padding: 0;

  &:hover {
    background: ${({ $active }) => ($active ? "#D85907" : "#9ca3af")};
  }

  &:focus-visible {
    outline: 2px solid #d85907;
    outline-offset: 2px;
  }

  [data-theme="dark"] & {
    background: ${({ $active }) =>
      $active ? "#D85907" : "rgba(255,255,255,0.25)"};

    &:hover {
      background: ${({ $active }) =>
        $active ? "#D85907" : "rgba(255,255,255,0.45)"};
    }
  }
`;

// Compute the sliding dot window. Always returns up to MAX_VISIBLE_DOTS
// indexes centered on pageIdx, pinned to the edges when near the ends.
function computeDotWindow(pageIdx, totalPages) {
  if (totalPages <= MAX_VISIBLE_DOTS) {
    return Array.from({ length: totalPages }, (_, i) => i);
  }
  const half = Math.floor(MAX_VISIBLE_DOTS / 2);
  let start = pageIdx - half;
  if (start < 0) start = 0;
  if (start + MAX_VISIBLE_DOTS > totalPages)
    start = totalPages - MAX_VISIBLE_DOTS;
  return Array.from({ length: MAX_VISIBLE_DOTS }, (_, i) => start + i);
}

function PagedCarousel({
  items,
  perPage,
  pageIdx,
  totalPages,
  onPageChange,
  loading,
  renderItem,
  renderSkeleton,
  subgridRows,
  ariaLabel = "carousel",
  emptyMessage = "Belum ada konten untuk ditampilkan.",
}) {
  const go = React.useCallback(
    (next) => {
      if (next < 0 || next >= totalPages) return;
      onPageChange(next);
    },
    [onPageChange, totalPages],
  );

  const visibleDots = React.useMemo(
    () => computeDotWindow(pageIdx, totalPages),
    [pageIdx, totalPages],
  );

  const showEmptyState =
    !loading && totalPages === 0 && (!items || items.length === 0);

  // Stable identities for the loading placeholders. They are positional by
  // nature — there is no data to key on — so the keys are precomputed once per
  // `perPage` instead of derived from the map index at render time, which is
  // what makes them stable across the loading re-renders.
  const skeletonKeys = React.useMemo(
    () => Array.from({ length: perPage }, (_, i) => `sk-${i}`),
    [perPage],
  );

  return (
    <Wrap aria-label={ariaLabel}>
      <Viewport>
        {showEmptyState ? (
          <EmptyState role="status">{emptyMessage}</EmptyState>
        ) : (
          <Track key={pageIdx} $perPage={perPage} $subgridRows={subgridRows}>
            {loading
              ? skeletonKeys.map((skeletonKey, i) =>
                  renderSkeleton ? (
                    <React.Fragment key={skeletonKey}>
                      {renderSkeleton(i)}
                    </React.Fragment>
                  ) : (
                    <SkeletonCell key={skeletonKey} />
                  ),
                )
              : items.map((it, i) => renderItem(it, i))}
          </Track>
        )}
      </Viewport>
      {totalPages > 1 && (
        <>
          <SideArrow
            type="button"
            $dir="prev"
            aria-label="Sebelumnya"
            disabled={pageIdx === 0}
            onClick={() => go(pageIdx - 1)}
          >
            ‹
          </SideArrow>
          <SideArrow
            type="button"
            $dir="next"
            aria-label="Selanjutnya"
            disabled={pageIdx >= totalPages - 1}
            onClick={() => go(pageIdx + 1)}
          >
            ›
          </SideArrow>
          <Controls>
            <Dots role="tablist" aria-label="Indicator halaman">
              {visibleDots.map((i) => (
                <Dot
                  key={i}
                  type="button"
                  role="tab"
                  $active={i === pageIdx}
                  aria-label={`Halaman ${i + 1}`}
                  aria-selected={i === pageIdx}
                  onClick={() => go(i)}
                />
              ))}
            </Dots>
          </Controls>
        </>
      )}
    </Wrap>
  );
}

export default React.memo(PagedCarousel);
